import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchClient } from '@eureca/backoffice-api-sdk';
import { formatInstantBR } from '@/lib/format-date';
import { Badge, Button, cn, Skeleton, toast } from '@eureca/ui';
import type { BackofficeUser } from '../../../../shared/users';
import { Section } from '@/shared/section';
import { AccountTab } from '@/features/account/AccountTab';
import { InscricoesSection } from '@/features/inscriptions';
import { AuditLogList } from '@/features/audit';
import { isAdmin } from '@/auth/roles';
import { AvatarCard } from '../../../../features/profile/AvatarCard';
import { PhoneCard } from '../../../../features/profile/PhoneCard';
import { ResumeCard } from '../../../../features/profile/ResumeCard';
import { ProfileEditSection } from '../../../../features/profile/ProfileEditSection';
import { FormationCard } from '../../../../features/profile/FormationCard';
import {
  useGetProfile,
  isCoreProfileOrphan,
  useProvisionCoreProfile,
} from '../../../../features/profile/use-profile';
import { str } from '../../../../features/profile/profile-readonly';

function useGetUser(id: string) {
  return useQuery({
    queryKey: ['backoffice', 'users', id],
    queryFn: async () => {
      const res = await fetchClient<BackofficeUser>({
        method: 'GET',
        url: `/users/${id}`,
      });
      return res.data;
    },
  });
}

// ---------------------------------------------------------------------------
// Inscrições — a listagem clicável + a tela de detalhe vivem em
// `@/features/inscriptions` (InscricoesSection / InscriptionDetail).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Logs — GET /users/:id/events → KcEvent[] (eventos do Keycloak).
// ---------------------------------------------------------------------------

interface KcEvent {
  time?: number;
  type?: string;
  clientId?: string;
  ipAddress?: string;
  error?: string;
}

// Rótulos PT-BR dos tipos de evento do Keycloak mais comuns.
const KC_EVENT_LABELS: Record<string, string> = {
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  REGISTER: 'Cadastro',
  LOGIN_ERROR: 'Falha no login',
  REGISTER_ERROR: 'Falha no cadastro',
  UPDATE_PROFILE: 'Perfil atualizado',
  UPDATE_EMAIL: 'Email atualizado',
  UPDATE_PASSWORD: 'Senha atualizada',
  RESET_PASSWORD: 'Senha redefinida',
  SEND_RESET_PASSWORD: 'Envio de redefinição de senha',
  SEND_VERIFY_EMAIL: 'Envio de verificação de email',
  VERIFY_EMAIL: 'Email verificado',
  IDENTITY_PROVIDER_LOGIN: 'Login social',
  IDENTITY_PROVIDER_FIRST_LOGIN: 'Primeiro login social',
  CODE_TO_TOKEN: 'Troca de código por token',
  REFRESH_TOKEN: 'Renovação de token',
};

function eventLabel(type: string | undefined): string {
  if (!type) return 'Evento';
  return KC_EVENT_LABELS[type] ?? type;
}

function useGetUserEvents(id: string) {
  return useQuery({
    queryKey: ['backoffice', 'users', id, 'events'],
    queryFn: async () => {
      const res = await fetchClient<KcEvent[]>({
        method: 'GET',
        url: `/users/${id}/events`,
      });
      return res.data;
    },
  });
}

function useDisablePhoneVerification(id: string) {
  return useMutation({
    mutationFn: async () => {
      await fetchClient<null>({
        method: 'POST',
        url: `/users/${id}/disable-phone-verification`,
      });
    },
  });
}

function formatName(u: BackofficeUser): string {
  const parts = [u.firstName, u.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : (u.username ?? '—');
}

// ── Logs (Keycloak events) — plain scrollable list ───────────────────────────
function LogsSection({ userId }: { userId: string }) {
  const eventsQuery = useGetUserEvents(userId);

  return (
    <Section title="Logs">
      {eventsQuery.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {eventsQuery.isError && (
        <p className="text-xs text-destructive">Não foi possível carregar os logs.</p>
      )}
      {eventsQuery.data && eventsQuery.data.length === 0 && (
        <p className="text-xs text-muted-foreground">Nenhum evento.</p>
      )}
      {eventsQuery.data && eventsQuery.data.length > 0 && (
        <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {eventsQuery.data.map((ev, i) => (
            <li key={`${ev.time ?? i}-${i}`} className="text-sm">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={cn('font-medium', ev.error ? 'text-destructive' : 'text-foreground')}
                >
                  {eventLabel(ev.type)}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatInstantBR(ev.time, { withTime: true })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground/80">
                {[ev.clientId, ev.ipAddress].filter(Boolean).join(' · ') || '—'}
              </p>
              {ev.error && <p className="text-xs text-destructive">{ev.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

type UserTab = 'perfil' | 'inscricoes' | 'auditoria' | 'logs' | 'conta';

const USER_TABS: {
  key: UserTab;
  label: string;
  /** Selo ao lado do rótulo — sinaliza a aba gateada de forma VISÍVEL (não só no tooltip). */
  badge?: string;
  disabled?: boolean;
  adminOnly?: boolean;
  hidden?: boolean;
}[] = [
  { key: 'perfil', label: 'Perfil' },
  { key: 'inscricoes', label: 'Inscrições' },
  { key: 'auditoria', label: 'Auditoria', adminOnly: true },
  // Aba "Logs" — visível porém DESABILITADA, com selo "Em breve" (EU-519): a tela
  // de logs é história própria. `LogsSection` + o body `tab === 'logs'` ficam no
  // arquivo, prontos para quando a história entrar (basta remover `disabled` + `badge`).
  { key: 'logs', label: 'Logs', badge: 'Em breve', disabled: true },
  { key: 'conta', label: 'Conta' },
];

function UsuarioDetailPage() {
  const { id } = Route.useParams();

  const { data: user, isLoading, isError } = useGetUser(id);
  // Nome completo vem do perfil (Core `name` — o campo "Nome completo" da aba
  // Perfil); fallback para o nome do Keycloak quando não há perfil.
  const profileQuery = useGetProfile(id);
  const provisionCoreProfileMutation = useProvisionCoreProfile(id);
  const disablePhoneMutation = useDisablePhoneVerification(id);

  const [tab, setTab] = useState<UserTab>('perfil');

  // Âncora vinda do atalho do modal da trilha (#curriculo): rola até a seção
  // quando o conteúdo termina de carregar (o elemento só existe pós-load; a
  // aba default já é 'perfil', onde o card vive).
  useEffect(() => {
    if (isLoading || tab !== 'perfil') return;
    const anchor = window.location.hash.slice(1);
    if (!anchor) return;
    // Próximo frame — garante o card montado antes do scroll.
    requestAnimationFrame(() => {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [isLoading, tab]);

  // The "Auditoria" tab is admin-only; drop it (and never render its body) for
  // non-admin operators. `tab` defaults to 'perfil', so it can't land there.
  const admin = isAdmin();
  const visibleTabs = USER_TABS.filter((t) => (!t.adminOnly || admin) && !t.hidden);

  // Provisiona o candidato órfão no Core. `ensureUser` cria a linha-base (síncrono →
  // o gate some no refetch); o candidato completa o próprio perfil no App v2.
  async function handleImportCoreProfile() {
    try {
      await provisionCoreProfileMutation.mutateAsync();
      toast.success('Perfil criado no Core. O candidato pode completar os dados no app.');
    } catch {
      toast.error('Não foi possível importar o perfil do Core.');
    }
  }

  async function handleDisablePhoneVerification() {
    try {
      await disablePhoneMutation.mutateAsync();
      toast.success('Verificação de telefone desabilitada.');
    } catch {
      toast.error('Não foi possível desabilitar a verificação de telefone.');
    }
  }

  return (
    <div className="p-8">
      {/* Volta pra listagem, saindo do usuário selecionado. Mesmo padrão do
          back link do detalhe da inscrição. */}
      <Link
        to="/usuarios"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="usuario-detail-back"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-4"
          aria-hidden="true"
        >
          <path d="m12 19-7-7 7-7" />
          <path d="M19 12H5" />
        </svg>
        Voltar para usuários
      </Link>

      {isError && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-4 shrink-0"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Não foi possível carregar o usuário. Tente novamente.
        </div>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 items-start">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="size-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-40" />
              </div>
            </div>
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      )}

      {user && (
        <div className="space-y-4">
          {/* Cabeçalho — sempre visível acima das abas */}
          <AvatarCard user={user}>
            <div className="min-w-0 space-y-0.5">
              <h1 className="text-lg font-semibold text-foreground">
                {str(profileQuery.data?.personalData ?? null, 'name').trim() || formatName(user)}
              </h1>
              <p className="truncate text-sm text-muted-foreground">{user.email ?? '—'}</p>
            </div>
          </AvatarCard>

          {/* Abas */}
          <div className="flex gap-1 border-b border-border" role="tablist">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                aria-disabled={t.disabled}
                disabled={t.disabled}
                title={t.badge}
                onClick={() => {
                  if (!t.disabled) setTab(t.key);
                }}
                className={cn(
                  'relative -mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  t.disabled
                    ? 'cursor-not-allowed border-transparent text-muted-foreground/40'
                    : tab === t.key
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
                data-testid={`usuario-tab-${t.key}`}
              >
                {t.label}
                {t.badge && (
                  <Badge
                    variant="outline"
                    className="border-border/60 px-1.5 py-0 text-[10px] leading-4 text-muted-foreground"
                    data-testid={`usuario-tab-${t.key}-badge`}
                  >
                    {t.badge}
                  </Badge>
                )}
              </button>
            ))}
          </div>

          {/* ── Aba: Perfil (READ-ONLY) ──
              A edição de perfil foi aposentada com o decomissionamento da
              coexistência (spec 038, Fase 2) — o candidato edita o próprio perfil
              no App v2. O backoffice consulta os dados do Core; foto, currículo e
              telefone têm cards próprios (foto/currículo via os endpoints de
              sistema do candidate BFF, EU-582; telefone read-only + desabilitar
              verificação via KC). */}
          {tab === 'perfil' && (
            <div className="text-sm">
              {/* Candidato órfão no Core — importar cria a linha-base a partir do KC.
                  Visível SÓ quando o Core não tem perfil (personalData:null). */}
              {isCoreProfileOrphan(profileQuery.data) && (
                <section
                  className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 p-3"
                  data-testid="usuario-core-orphan-callout"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      Candidato sem perfil no Core
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Existe no Keycloak mas ainda não tem perfil na nova plataforma. Importar cria
                      a linha-base a partir do Keycloak; o candidato completa os dados no app.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => void handleImportCoreProfile()}
                    disabled={provisionCoreProfileMutation.isPending}
                    data-testid="usuario-import-core-profile"
                  >
                    {provisionCoreProfileMutation.isPending
                      ? 'Importando…'
                      : 'Importar perfil do Core'}
                  </Button>
                </section>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 items-start">
                <div className="space-y-12">
                  <ProfileEditSection userId={id} section="identity" title="Dados pessoais" />
                  <PhoneCard
                    userId={id}
                    onDisablePhoneVerification={() => void handleDisablePhoneVerification()}
                    disablePending={disablePhoneMutation.isPending}
                  />
                  <ProfileEditSection userId={id} section="location" title="Localidade" />
                  <ProfileEditSection userId={id} section="diversity" title="Diversidade" />
                  {/* Âncora do atalho "Editar currículo do candidato" (modal da
                      trilha — ModuleAnswer navega com hash #curriculo). */}
                  <div id="curriculo" className="scroll-mt-6">
                    <Section title="Currículo">
                      <ResumeCard userId={id} />
                    </Section>
                  </div>
                </div>
                <div className="space-y-12">
                  <FormationCard userId={id} title="Formação" />
                </div>
              </div>
            </div>
          )}

          {/* ── Aba: Inscrições ── (grid full-width, sem max-w) */}
          {tab === 'inscricoes' && (
            <div className="text-sm">
              <InscricoesSection userId={id} />
            </div>
          )}

          {/* ── Aba: Auditoria (admin-only) ── */}
          {tab === 'auditoria' && admin && (
            <div className="max-w-2xl text-sm">
              <AuditLogList targetUserSub={id} />
            </div>
          )}

          {/* ── Aba: Logs ── */}
          {tab === 'logs' && (
            <div className="max-w-2xl text-sm">
              <LogsSection userId={id} />
            </div>
          )}

          {/* ── Aba: Conta ── */}
          {tab === 'conta' && <AccountTab user={user} userId={id} />}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_auth/_shell/usuarios/$id')({
  component: UsuarioDetailPage,
});
